import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select, FormControl } from "@mui/material";
import Label from "../../../components/Label/Label";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductsSitesAndUnitOfOperations } from "../../../redux/visualization/actions";
import "./analyticsHeader.scss";
import { setProductSitesUopFilters } from "../../../redux/shared/actions";

const renderDropdown = (
  list,
  label,
  value,
  onChange,
  disabled = false,
  disabledFromParent = false,
  customWidth
) => {
  const valid = list.some((i) => i.id === value?.id);
  const selectValue = valid ? value.id : "";

  return (
    <Box className="product-selector-container">
      <Box
        className="product-selector"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Label text={label} color="white" />
        <FormControl
          size="small"
          variant="outlined"
          sx={{ width: customWidth || "250px", marginLeft: "auto" }}
        >
          <Select
            value={selectValue}
            sx={{
              backgroundColor: "#12437c",
              borderRadius: "8px",
              ".MuiSelect-select": { display: "flex", alignItems: "center" },
            }}
            onChange={(e) => {
              const selectedId = e.target.value;
              const selectedItem = list.find((item) => item.id === selectedId);
              const newValue =
                selectedId && selectedItem
                  ? { id: selectedItem.id, name: selectedItem.name, is_multi_approve: selectedItem.is_multi_approve }
                  : { id: "", name: "" };
              onChange(newValue);
            }}
            displayEmpty
            className="product-select"
            MenuProps={{ PaperProps: { className: "dropdown-paper" } }}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Label width="100%" text={`Select ${label}`} color="white" />
                );
              }
              const item = list.find((i) => i.id === selected);
              return (
                <Label text={item?.name || `Select ${label}`} color="white" />
              );
            }}
            disabled={disabled || disabledFromParent}
          >
            {list.map((item) => (
              <MenuItem
                key={item.id}
                value={item.id}
                className={value?.id === item.id ? "selected-item" : ""}
              >
                <Label text={item.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default function DropdownsBar({
  isUnitOpHide,
  disabledFromParent = false,
}) {
  const dispatch = useDispatch();

  const {
    product = { id: "", name: "", is_multi_approve: '' },
    site = { id: "", name: "" },
    unitOfOperation = { id: "", name: "" },
  } = useSelector((state) => state.shared.productSitesUopValues || {});

  const { data = [] } = useSelector((state) => {
    const list = state?.viz?.productsAndSitesList;
    return { data: Array.isArray(list?.data) ? list.data : [] };
  });

  const productList = useMemo(
    () => data.map((p) => ({ id: p.product_id, name: p.product_name, is_multi_approve: p.is_multi_approve })),
    [data]
  );

  const sitesList = useMemo(() => {
    if (!product.id) return [];
    const matched = data.find((p) => p.product_id === product.id);
    return (
      matched?.sites?.map((s) => ({ id: s.site_id, name: s.site_name })) || []
    );
  }, [data, product]);

  const unitOfOperationList = useMemo(() => {
    if (!product.id || !site.id) return [];
    const matchedProduct = data.find((p) => p.product_id === product.id);
    const matchedSite = matchedProduct?.sites?.find(
      (s) => s.site_id === site.id
    );
    return (
      matchedSite?.uom?.map((u) => ({ id: u.unit_op_id, name: u.step_name })) ||
      []
    );
  }, [data, product, site]);


  useEffect(() => {
    if (!data.length) return;

    // find updated product
    const updatedProduct = data.find(
      (p) => p.product_id === product?.id
    );

    if (!updatedProduct) return;

    const newProduct = {
      id: updatedProduct.product_id,
      name: updatedProduct.product_name,
      is_multi_approve: updatedProduct.is_multi_approve,
    };

    // find updated site
    const updatedSite = updatedProduct?.sites?.find(
      (s) => s.site_id === site?.id
    );

    const newSite = updatedSite
      ? { id: updatedSite.site_id, name: updatedSite.site_name }
      : { id: "", name: "" };

    // find updated unit operation
    const updatedUop = updatedSite?.uom?.find(
      (u) => u.unit_op_id === unitOfOperation?.id
    );

    const newUop = updatedUop
      ? { id: updatedUop.unit_op_id, name: updatedUop.step_name }
      : { id: "", name: "" };

    dispatch(
      setProductSitesUopFilters({
        product: newProduct,
        site: newSite,
        unitOfOperation: newUop,
      })
    );
  }, [data]);
  
  useEffect(() => {
    if (!data.length) {
      dispatch(fetchProductsSitesAndUnitOfOperations());
    }
  }, [dispatch, data.length]);

  return (
    <Box sx={{ display: "flex", gap: 2, px: 1, py: 1, paddingTop: "0px" }}>
      {renderDropdown(
        productList,
        "Product",
        product,
        (value) => {
          dispatch(
            setProductSitesUopFilters({
              product: value,
              site: { id: "", name: "" },
              unitOfOperation: { id: "", name: "" },
            })
          );
        },
        "",
        disabledFromParent,
        "250px"
      )}
      {renderDropdown(
        sitesList,
        "Manufacturing Site",
        site,
        (value) => {
          dispatch(
            setProductSitesUopFilters({
              product,
              site: value,
              unitOfOperation: { id: "", name: "" },
            })
          );
        },
        !product?.id,
        disabledFromParent,
        "250px"
      )}
      {!isUnitOpHide &&
        renderDropdown(
          unitOfOperationList,
          "Unit Operation",
          unitOfOperation,
          (value) => {
            dispatch(
              setProductSitesUopFilters({
                product,
                site,
                unitOfOperation: value,
              })
            );
          },
          !site?.id,
          disabledFromParent,
          "350px"
        )}
    </Box>
  );
}
